import isEqual from 'lodash/isEqual.js';
import omit from 'lodash/omit.js';
import { z } from 'zod';

import { ReportGridFilterModelSchema } from './report-filter';
import type { ReportGridFilterModel } from './report-filter';
import { reportPreviewColumnsSchema } from './report-preview';

/**
 * Answers (documents times questions) past which "Create and run" asks the
 * user to confirm the run in a dialog. The server refuses an approval past it
 * without `largeRunConfirmed`, so a stale or edited client cannot skip it.
 */
export const LARGE_RUN_ANSWER_COUNT = 2000;

/** Why a question run was proposed. The stored proposal and the run card share this list. */
export const QUESTION_RUN_ORIGINS = ['notRun', 'reportRun', 'uncovered'] as const;

/** How many answers a run produces: one per document per question. */
export function getRunAnswerCount({
  documentCount,
  questionCount,
}: {
  documentCount: number;
  questionCount: number;
}): number {
  return documentCount * questionCount;
}

export function getIsLargeRun(answerCount: number): boolean {
  return answerCount > LARGE_RUN_ANSWER_COUNT;
}

/**
 * The run card's input, as the server writes it before asking the user: the
 * proposal's existing questions, the agent's drafts resolved to canonical
 * columns, and the counts. The approved copy, with the card's edits to the
 * drafts, is what the run tool executes.
 */
export const questionRunApprovalInputSchema = z.object({
  /** Set by the server when the org has no AI tokens left; the card disables the run. */
  aiUsageBlockedReason: z.string().optional(),
  documentCountAtProposal: z.number().int().nonnegative(),
  /** Existing questions the proposal runs. Shown read-only. */
  existingQuestions: z.array(z.object({ id: z.string(), title: z.string() })).default([]),
  /**
   * Set when the answer after the run keeps filters the card does not show,
   * such as filters on questions the analysis read. They join the card's
   * filters under the proposal's AND/OR, so the card cannot change it.
   */
  filterLogicOperatorLocked: z.boolean().optional(),
  /**
   * The documents the run covers, without filters on the questions it runs.
   * The user can edit it on the card; approvals saved before it existed have none.
   */
  filterModel: ReportGridFilterModelSchema.optional(),
  largeRunConfirmed: z.boolean().optional(),
  missingFacts: z.array(z.string()).optional(),
  /** Drafted questions, and existing ones the agent reused by ref. */
  newQuestions: reportPreviewColumnsSchema.default([]),
  origin: z.enum(QUESTION_RUN_ORIGINS).default('notRun'),
  /** Every question's title, for the card's one-line copy. */
  questionTitles: z.array(z.string()).default([]),
  runProposalId: z.string().min(1),
});

export type QuestionRunApprovalInput = z.output<typeof questionRunApprovalInputSchema>;

/** Field names the server and the card both write. */
export const QUESTION_RUN_APPROVAL_FIELDS = {
  filterModel: 'filterModel',
  largeRunConfirmed: 'largeRunConfirmed',
  newQuestions: 'newQuestions',
} satisfies Partial<{ [TKey in keyof QuestionRunApprovalInput]: TKey }>;

/** The keys the card writes back; the server sets `aiUsageBlockedReason` and the card drops it. */
const CARD_EDITED_KEYS = [
  'aiUsageBlockedReason',
  QUESTION_RUN_APPROVAL_FIELDS.filterModel,
  QUESTION_RUN_APPROVAL_FIELDS.largeRunConfirmed,
  QUESTION_RUN_APPROVAL_FIELDS.newQuestions,
] satisfies (keyof QuestionRunApprovalInput)[];

/**
 * Whether an approved run card input makes only the edits the card offers on
 * the input the server wrote: leaving drafts out, changing the filters, and
 * confirming a large run. Leaving a draft out drops the inputs other drafts
 * read from it. Anything else would run questions the user never saw. An
 * input saved without filters offers no filter editor, so it cannot gain one,
 * and a locked AND/OR stays as the server wrote it.
 */
export function getIsQuestionRunApprovalEdit({
  approved,
  persisted,
}: {
  approved: unknown;
  persisted: unknown;
}): boolean {
  const persistedInput = questionRunApprovalInputSchema.safeParse(persisted);
  const approvedInput = questionRunApprovalInputSchema.safeParse(approved);
  if (!persistedInput.success || !approvedInput.success) return false;
  if (
    !isEqual(
      omit(approvedInput.data, CARD_EDITED_KEYS),
      omit(persistedInput.data, CARD_EDITED_KEYS)
    )
  )
    return false;
  if (Boolean(approvedInput.data.filterModel) !== Boolean(persistedInput.data.filterModel))
    return false;
  if (
    persistedInput.data.filterLogicOperatorLocked &&
    getLogicOperator(approvedInput.data.filterModel) !==
      getLogicOperator(persistedInput.data.filterModel)
  )
    return false;

  const approvedDrafts = approvedInput.data.newQuestions;
  const draftsByKey = new Map(persistedInput.data.newQuestions.map(draft => [draft.key, draft]));
  if (new Set(approvedDrafts.map(draft => draft.key)).size !== approvedDrafts.length) return false;
  return approvedDrafts.every(draft => {
    const persistedDraft = draftsByKey.get(draft.key);
    return (
      Boolean(persistedDraft) &&
      isEqual(omit(draft, 'inputColumnKeys'), omit(persistedDraft, 'inputColumnKeys')) &&
      draft.inputColumnKeys.every(key => persistedDraft?.inputColumnKeys.includes(key))
    );
  });
}

/** A filter's AND/OR, with the grid's default of AND when it names none. */
export function getLogicOperator(filterModel: ReportGridFilterModel | undefined) {
  return filterModel?.logicOperator ?? 'and';
}
